/**
 * Transparent axios response cache + deduplication.
 *
 * Caches GET responses in-memory for a short TTL (default: 15 s for most
 * endpoints, 60 s for immutable lookups like /api/v2/enseignes, categories,
 * audiences).
 *
 * Identical concurrent GETs are deduplicated: a second GET to the same URL
 * while the first is in-flight will reuse the same promise.
 *
 * Any non-GET request (POST/PUT/PATCH/DELETE) clears the entire cache so that
 * the next read always returns fresh data. This is safe because cross-resource
 * invalidation (e.g. PUT /schedules/* → GET /planning/*) is very common here.
 *
 * Safe to remove: deleting the import in `App.js` disables the cache entirely.
 */
import axios from 'axios';

const RESPONSE_CACHE = new Map();   // key -> { expiresAt, data, status }
const INFLIGHT = new Map();         // key -> Promise
let generation = 0;

// URLs that almost never change — cache longer
const LONG_TTL_PATTERNS = [
  /\/tutorials\/categories/,
  /\/tutorials\/audiences/,
  /\/enseignes\b/,
  /\/my-enseigne/,
  /\/enseigne-by-slug\//,
];

// URLs we never want to cache (realtime data)
const NO_CACHE_PATTERNS = [
  /\/auth\//,
  /\/stripe\//,
  /\/upload/i,
  /\/files\//,          // signed URLs / streams
  /\/download/i,
  /\/ws\//,
  /\/progress\b/,       // tutorial script progress
  /\/logs\b/,
];

const DEFAULT_TTL = 15 * 1000;
const LONG_TTL = 60 * 1000;
// Stale-while-revalidate : au-delà du TTL "frais", une réponse déjà vue (< STALE_MAX) est
// rendue immédiatement puis rafraîchie en arrière-plan (événement `api-cache:refreshed`).
const STALE_MAX = 5 * 60 * 1000;
const PERSIST_KEY = 'logyra_api_cache_v1';
const PERSIST_MAX_ENTRY = 150 * 1024;

const loadPersisted = () => {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const now = Date.now();
    Object.entries(JSON.parse(raw)).forEach(([k, v]) => { if (v.staleUntil > now) RESPONSE_CACHE.set(k, { ...v, expiresAt: 0 }); });
  } catch { /* ignore */ }
};
let persistTimer = null;
const persistSoon = () => {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      const out = {};
      RESPONSE_CACHE.forEach((v, k) => { if (v.size <= PERSIST_MAX_ENTRY) out[k] = { data: v.data, status: v.status, staleUntil: v.staleUntil, size: v.size }; });
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify(out));
    } catch { /* quota */ }
  }, 800);
};
if (typeof window !== 'undefined') loadPersisted();

const revalidate = (config, key) => {
  const req = { ...config, adapter: undefined, skipCache: true, __swrRefresh: true, __cacheKey: key, headers: { ...(config.headers || {}) } };
  axios(req).then((res) => {
    const size = JSON.stringify(res.data || '').length;
    RESPONSE_CACHE.set(key, { data: res.data, status: res.status, headers: res.headers, expiresAt: Date.now() + getTtl(config.url || ''), staleUntil: Date.now() + STALE_MAX, size });
    persistSoon();
    window.dispatchEvent(new CustomEvent('api-cache:refreshed', { detail: { url: config.url, params: config.params, key, data: res.data } }));
  }).catch(() => {});
};

const getTtl = (url) => {
  if (LONG_TTL_PATTERNS.some(rx => rx.test(url))) return LONG_TTL;
  return DEFAULT_TTL;
};

const isCacheable = (config) => {
  if ((config.method || 'get').toLowerCase() !== 'get') return false;
  const url = config.url || '';
  if (NO_CACHE_PATTERNS.some(rx => rx.test(url))) return false;
  if (config.skipCache) return false;
  return true;
};

const keyFor = (config) => {
  const auth = config.headers?.Authorization || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.url}|${params}|${auth}`;
};

// --- Invalidation on writes --------------------------------------------------
// Any non-GET request clears the ENTIRE cache. This is aggressive but it's the
// only reliable strategy when writes and reads use different URL prefixes
// (e.g. PUT /api/v2/schedules/... must invalidate GET /api/v2/planning/...).
// The 60s cache is a minor optimization; freshness after mutations matters more.
// Écritures « télémétrie » qui ne modifient aucune donnée affichée : ne vident pas le cache.
const NO_INVALIDATE_PATTERNS = [/\/track\//, /\/auth\/heartbeat/, /\/auth\/me\b/, /\/page-visit/, /\/push\/subscribe/, /\/notifications\/unread-count/, /\/_meta\//];

const invalidateFor = (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get') return;
  if (config.__swrRefresh) return;
  if (NO_INVALIDATE_PATTERNS.some(rx => rx.test(config.url || ''))) return;
  generation += 1;
  RESPONSE_CACHE.clear();
  INFLIGHT.clear();
  try { sessionStorage.removeItem(PERSIST_KEY); } catch { /* ignore */ }
};

// --- Request interceptor -----------------------------------------------------
axios.interceptors.request.use((config) => {
  invalidateFor(config);
  if (!isCacheable(config)) return config;
  const key = keyFor(config);
  const hit = RESPONSE_CACHE.get(key);
  if (hit && (hit.expiresAt > Date.now() || (hit.staleUntil || 0) > Date.now())) {
    const stale = hit.expiresAt <= Date.now();
    if (stale) { hit.expiresAt = Date.now() + 2000; revalidate(config, key); }
    // Short-circuit: return a canned response via adapter
    config.adapter = () => Promise.resolve({
      data: hit.data,
      status: hit.status,
      statusText: stale ? 'OK (stale cache)' : 'OK (cache)',
      headers: hit.headers || {},
      config,
      request: null,
      fromCache: true,
      stale,
    });
    return config;
  }
  config.__cacheKey = key;
  config.__cacheGeneration = generation;
  // Independent abort signals must never cancel another screen's transport.
  if (!config.signal && !config.cancelToken) {
    const transport = config.__originalReadAdapter || axios.getAdapter(config.adapter || axios.defaults.adapter);
    config.__originalReadAdapter = transport;
    config.adapter = async request => {
      const flightKey = `${request.__cacheGeneration}|${key}`;
      let pending = INFLIGHT.get(flightKey);
      if (!pending) {
        pending = Promise.resolve().then(() => transport(request));
        INFLIGHT.set(flightKey, pending);
        const remove = () => { if (INFLIGHT.get(flightKey) === pending) INFLIGHT.delete(flightKey); };
        pending.then(remove, remove);
      }
      return { ...(await pending), config: request };
    };
  }
  return config;
});

// --- Response interceptor ----------------------------------------------------
axios.interceptors.response.use(
  (response) => {
    const config = response.config;
    if (isCacheable(config) && config.__cacheKey && config.__cacheGeneration === generation && response.status >= 200 && response.status < 300) {
      RESPONSE_CACHE.set(config.__cacheKey, {
        data: response.data,
        status: response.status,
        headers: response.headers,
        expiresAt: Date.now() + getTtl(config.url || ''),
        staleUntil: Date.now() + STALE_MAX,
        size: JSON.stringify(response.data || '').length,
      });
      persistSoon();
    }
    invalidateFor(config);
    return response;
  },
  (error) => {
    if (error.config) invalidateFor(error.config);
    return Promise.reject(error);
  }
);

// Manual invalidation API (available on window for debug)
export const clearApiCache = () => { generation += 1; RESPONSE_CACHE.clear(); INFLIGHT.clear(); try { sessionStorage.removeItem(PERSIST_KEY); } catch { /* ignore */ } };
if (typeof window !== 'undefined') window.__clearApiCache = clearApiCache;
